import React from 'react'
import { Button } from '@/components/ui/button'

interface ComplianceIssue {
  id?: number
  rule: string
  severity: 'critical' | 'high' | 'medium' | 'warning' | 'minor' | 'info'
  message: string
  status: 'pass' | 'fail' | 'warning' | 'not_checked'
  channel?: string
  field?: string
  section?: string
  detectedValue?: string
  expectedValue?: string
  source?: 'Built-in' | 'Custom BRD' | 'AI Analysis'
  reasoning?: string
  // Video-specific timestamp fields (from Gemini analysis)
  timestamp?: string // "MM:SS" or "HH:MM:SS" format
  timestampSeconds?: number // For video seeking
  endTimestamp?: string
  endTimestampSeconds?: number
  duration?: number // Duration in seconds
  videoFrame?: string // Description of what's in the frame
  audioContent?: string // What's being said at this time
  coordinates?: { x: number; y: number; width: number; height: number }
}

interface ComplianceIssueCardProps {
  issue: ComplianceIssue
  index: number
  expanded: boolean
  debugMode: boolean
  onToggleExpanded: () => void
  onToggleDebug: () => void
  onApplyFix?: () => void
  onMarkReviewed?: () => void
  videoRef?: React.RefObject<HTMLVideoElement> // For video seeking
}

export default function ComplianceIssueCard({
  issue,
  expanded,
  debugMode,
  onToggleExpanded,
  onToggleDebug,
  onApplyFix,
  onMarkReviewed,
  videoRef
}: ComplianceIssueCardProps) {
  const isFailed = issue.status === 'fail'

  // Seek to timestamp in video
  const handleSeekToTimestamp = (e: React.MouseEvent) => {
    e.stopPropagation() // Don't toggle expansion
    if (videoRef?.current && issue.timestampSeconds !== undefined) {
      videoRef.current.currentTime = issue.timestampSeconds
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      videoRef.current.play()
      console.log(`🎬 Seeking to ${issue.timestamp} (${issue.timestampSeconds}s)`)
    }
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-850">
      {/* Issue Header - Clickable */}
      <button
        onClick={onToggleExpanded}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          <span className={`text-lg ${expanded ? 'rotate-90' : ''} transition-transform`}>▶</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            issue.status === 'pass' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            issue.status === 'fail' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
            issue.status === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
            'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            {issue.status?.toUpperCase()}
          </span>
          {issue.source && (
            <span className="px-2 py-0.5 rounded text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
              {issue.source}
            </span>
          )}
          {/* Video Timestamp Badge - Clickable */}
          {issue.timestamp && videoRef && (
            <button
              onClick={handleSeekToTimestamp}
              className="px-2 py-0.5 rounded text-xs font-mono bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/40 transition-colors flex items-center gap-1 border border-purple-300 dark:border-purple-700"
              title="Click to jump to this timestamp in the video"
            >
              <span>🎬</span>
              <span>{issue.timestamp}</span>
              {issue.duration && <span className="text-[10px] opacity-70">({issue.duration}s)</span>}
            </button>
          )}
          <span className="font-medium text-sm text-gray-900 dark:text-gray-100 text-left">
            {issue.rule}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {issue.channel || 'All'}
          </span>
          {issue.severity && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              issue.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
              issue.severity === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
              issue.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
              'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              {issue.severity}
            </span>
          )}
        </div>
      </button>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 py-3 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800/30 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {/* Message */}
          {issue.message && (
            <div className="bg-white dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Description</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {issue.message}
              </div>
            </div>
          )}
          
          {/* Reasoning (NEW) */}
          {issue.reasoning && (
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
              <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1">
                <span>💡</span>
                <span>How This Was Checked</span>
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400 leading-relaxed">
                {issue.reasoning}
              </div>
            </div>
          )}

          {/* Video-Specific Information */}
          {(issue.videoFrame || issue.audioContent || (issue.timestamp && issue.endTimestamp)) && (
            <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
              <div className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
                <span>🎬</span>
                <span>Video Context</span>
                {issue.timestamp && (
                  <span className="ml-auto font-mono bg-purple-200 dark:bg-purple-900/30 px-2 py-0.5 rounded">
                    {issue.timestamp}{issue.endTimestamp && ` - ${issue.endTimestamp}`}
                  </span>
                )}
              </div>
              
              {issue.videoFrame && (
                <div className="mb-2">
                  <div className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 mb-1">VISUAL SCENE:</div>
                  <div className="text-sm text-purple-700 dark:text-purple-300 leading-relaxed bg-white/50 dark:bg-black/20 rounded p-2">
                    {issue.videoFrame}
                  </div>
                </div>
              )}
              
              {issue.audioContent && (
                <div>
                  <div className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 mb-1">AUDIO TRANSCRIPT:</div>
                  <div className="text-sm text-purple-700 dark:text-purple-300 leading-relaxed bg-white/50 dark:bg-black/20 rounded p-2 italic">
                    "{issue.audioContent}"
                  </div>
                </div>
              )}

              {videoRef && issue.timestampSeconds !== undefined && (
                <Button
                  size="sm"
                  onClick={handleSeekToTimestamp}
                  className="mt-2 w-full bg-purple-600 hover:bg-purple-700 text-white text-xs h-7"
                >
                  🎬 Jump to {issue.timestamp} in Video
                </Button>
              )}
            </div>
          )}
          
          {/* Detected vs Expected - Side by Side */}
          {(issue.detectedValue || issue.expectedValue) && (
            <div className="grid grid-cols-2 gap-3">
              {issue.detectedValue && (
               <div>
                 <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1">
                   <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                   Current State
                 </div>
                 <div className="px-3 py-2.5 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 min-h-[80px] flex items-start leading-relaxed">
                   {issue.detectedValue}
                 </div>
               </div>
              )}
              
             {issue.expectedValue && (
               <div>
                 <div className={`text-xs font-semibold mb-1.5 flex items-center gap-1 ${
                   isFailed ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                 }`}>
                   <span className={`w-1.5 h-1.5 rounded-full ${
                     isFailed ? 'bg-red-500' : 'bg-green-500'
                   }`}></span>
                   {isFailed ? 'Required Fix' : 'Expected State'}
                 </div>
                 {isFailed ? (
                   <textarea
                     defaultValue={issue.expectedValue}
                     rows={3}
                     className="w-full px-3 py-2.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 dark:focus:ring-yellow-400 transition-all font-medium text-gray-900 dark:text-gray-100 leading-relaxed"
                     placeholder="Enter the correction needed for compliance..."
                   />
                 ) : (
                   <div className="px-3 py-2.5 bg-green-50/50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                     {issue.expectedValue}
                   </div>
                 )}
               </div>
             )}
            </div>
          )}
          
          {/* AI Analysis Log - Only visible in debug mode */}
          {debugMode && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                <span>🤖</span>
                <span>AI Analysis Log</span>
                <span className="ml-auto text-[10px] bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">DEBUG</span>
              </div>
              <div className="px-3 py-2.5 bg-gray-900 dark:bg-black rounded-lg text-xs font-mono space-y-1 overflow-x-auto">
                <div className="text-green-400">[{new Date().toLocaleTimeString()}] Analyzing rule: {issue.rule}</div>
                <div className="text-blue-400">[{new Date().toLocaleTimeString()}] Channel: {issue.channel || 'All'}</div>
                <div className="text-yellow-400">[{new Date().toLocaleTimeString()}] Severity: {issue.severity || 'N/A'}</div>
                <div className={`${
                  issue.status === 'pass' ? 'text-green-400' :
                  issue.status === 'fail' ? 'text-red-400' :
                  'text-yellow-400'
                }`}>
                  [{new Date().toLocaleTimeString()}] Result: {issue.status?.toUpperCase()}
                </div>
                {issue.detectedValue && (
                  <div className="text-gray-400">[{new Date().toLocaleTimeString()}] Detected: {issue.detectedValue.substring(0, 60)}...</div>
                )}
                {issue.reasoning && (
                  <div className="text-purple-400">[{new Date().toLocaleTimeString()}] Reasoning: {issue.reasoning.substring(0, 60)}...</div>
                )}
              </div>
            </div>
          )}
          
          {/* Action Buttons - Only for Failed Issues */}
          {isFailed && onApplyFix && onMarkReviewed && (
            <div className="flex gap-2 pt-1">
             <Button 
               size="sm" 
               className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs h-7"
               onClick={onApplyFix}
             >
               ✓ Apply Fix
             </Button>
             <Button 
               size="sm" 
               variant="outline" 
               className="text-xs h-7 border-gray-300 dark:border-gray-600"
               onClick={onMarkReviewed}
             >
               Mark as Reviewed
             </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-xs h-7 text-gray-500"
                onClick={onToggleExpanded}
              >
                Collapse
              </Button>
            </div>
          )}
          
          {/* Debug Mode Toggle - At bottom of each card */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onToggleDebug}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center gap-1.5"
            >
              <span className="text-[10px]">🐛</span>
              <span>{debugMode ? 'Hide' : 'Show'} Debug Log</span>
              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${
                debugMode ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {debugMode ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
          
          {/* Info Buttons - For Passed/Warning Issues */}
          {!isFailed && (
            <div className="flex justify-end">
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-xs h-7 text-gray-500"
                onClick={onToggleExpanded}
              >
                Collapse
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

